<script setup lang="ts">
import { stripBlockTags } from '@/composables/helpers/html';
const momentBrownleePng = '/img/2026/notable-moments/moment-brownlee.png';
const topShardPng = '/img/2026/notable-moments/top-shard.png';
const topShardMobilePng = '/img/2026/notable-moments/top-shard-mobile.png';


interface CardButton {
    label?: string;
    title?: string;
    url?: string;
}

interface CarouselCard {
    image?: string;
    mobileImage?: string;
    headline?: string;
    copy?: string;
    button?: CardButton;
    eyebrowText?: string;
}

const props = withDefaults(defineProps<{
    headline?: string;
    carouselCards?: CarouselCard[];
}>(), {
    headline: '',
});

const defaultSlides = [
    {
        headline: 'Headline about notable moment.',
        text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed vel est in felis malesuada dictum. Aliquam gravida, metus sed consequat varius, justo libero porta ligula, sed sagittis elit urna quis nisi. Vivamus id ante congue elit consectetur maximus. Vivamus a consectetur tortor, quis interdum arcu.',
        buttonLabel: 'Optional Link',
        buttonUrl: '#',
        image: momentBrownleePng,
        imageAlt: 'Two speakers in conversation on stage',
    },
    {
        headline: 'Another remarkable highlight.',
        text: 'Praesent euismod justo at libero tincidunt, vel faucibus nunc vestibulum. Curabitur vel felis non risus pharetra fermentum. Donec sit amet eros at lacus consequat feugiat vel nec nulla.',
        buttonLabel: 'Learn More',
        buttonUrl: '#',
        image: momentBrownleePng,
        imageAlt: 'Conference attendees networking',
    },
    {
        headline: 'A moment that inspired thousands.',
        text: 'Fusce dignissim lacus vitae eros elementum, at consequat sapien tincidunt. Integer euismod augue nec velit fermentum, sit amet hendrerit magna consequat. Nullam condimentum elit vel lorem pharetra.',
        buttonLabel: 'Watch Now',
        buttonUrl: '#',
        image: momentBrownleePng,
        imageAlt: 'Keynote speaker on main stage',
    },
];

const slides = computed(() => {
    if (props.carouselCards?.length) {
        return props.carouselCards.map(card => ({
            headline: card.headline || '',
            text: card.copy || '',
            buttonLabel: card.button?.label || '',
            buttonUrl: card.button?.url || '#',
            image: card.image?.src || card.image || card.mobileImage || momentBrownleePng,
            mobileImage: card.mobileImage || null,
            imageAlt: card.image?.alt || card.image?.altText || card.headline || '',
        }));
    }
    return defaultSlides;
});

const currentIndex = ref(0);
const slideDirection = ref('next');
const currentSlide = computed(() => slides.value[currentIndex.value]);

// A single slide is not a carousel — hide the controls and skip autoplay.
const hasMultiple = computed(() => slides.value.length > 1);

const SLIDE_DURATION = 10;
const CIRCUMFERENCE = 2 * Math.PI * 20;

const isPaused = ref(false);
const userPaused = ref(false);
const timerKey = ref(0);
let autoplayTimer = null;
let resumeTimer = null;
const reducedMotion = ref(false);

const imgTransitionName = computed(() =>
    slideDirection.value === 'prev' ? 'nm-img-prev' : 'nm-img',
);

const startAutoplay = () => {
    stopAutoplay();
    if (!hasMultiple.value) return;
    autoplayTimer = setInterval(() => {
        slideDirection.value = 'next';
        currentIndex.value = (currentIndex.value + 1) % slides.value.length;
        timerKey.value++;
    }, SLIDE_DURATION * 1000);
};

const stopAutoplay = () => {
    if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
    }
};

const togglePause = () => {
    userPaused.value = !userPaused.value;
    isPaused.value = userPaused.value;
    if (isPaused.value) {
        stopAutoplay();
    } else {
        timerKey.value++;
        startAutoplay();
    }
};

const isFirst = computed(() => currentIndex.value === 0);
const isLast = computed(() => currentIndex.value === slides.value.length - 1);

const goNext = () => {
    if (isLast.value) return;
    slideDirection.value = 'next';
    currentIndex.value = currentIndex.value + 1;
    timerKey.value++;
    if (!isPaused.value) startAutoplay();
};

const goPrev = () => {
    if (isFirst.value) return;
    slideDirection.value = 'prev';
    currentIndex.value = currentIndex.value - 1;
    timerKey.value++;
    if (!isPaused.value) startAutoplay();
};

// Lock image container height during out-in transition
const imgGridRef = ref(null);
function lockHeight(el) { el.style.minHeight = el.offsetHeight + 'px'; }
function unlockHeight(el) { el.style.minHeight = ''; }
function onImgBeforeLeave() { if (imgGridRef.value) lockHeight(imgGridRef.value); }
function onImgAfterEnter() { if (imgGridRef.value) unlockHeight(imgGridRef.value); }

// --- Text slide transition states ---
// All slides always in DOM, overlapping via CSS grid. The grid container
// automatically sizes to the tallest child — no JS measurement needed.
// Transitions are orchestrated by swapping state classes.
const leavingIndex = ref(null);
const enteringIndex = ref(null);
let transitionCleanup = null;

const getTextSlideClass = (index) => {
    const isNext = slideDirection.value === 'next';
    if (index === enteringIndex.value) return isNext ? 'nm-text-entering-next' : 'nm-text-entering-prev';
    if (index === currentIndex.value) return 'nm-text-active';
    if (index === leavingIndex.value) return isNext ? 'nm-text-leaving-next' : 'nm-text-leaving-prev';
    return 'nm-text-hidden';
};

watch(currentIndex, (newIdx, oldIdx) => {
    clearTimeout(transitionCleanup);
    leavingIndex.value = oldIdx;
    enteringIndex.value = newIdx;
    // Double rAF: ensures browser has painted the entering position
    // before we transition to active.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => { enteringIndex.value = null; });
    });
    transitionCleanup = setTimeout(() => { leavingIndex.value = null; }, 500);
});

// Shard slide-in animation
const sectionRef = ref(null);
const shardVisible = ref(false);

onMounted(() => {
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion.value) {
        isPaused.value = true;
    } else {
        setTimeout(() => startAutoplay(), 5000);
    }

    const observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                shardVisible.value = true;
                observer.disconnect();
            }
        },
        { threshold: 0.3 },
    );
    if (sectionRef.value) observer.observe(sectionRef.value);

    // Pause when off-screen, resume 1s after re-entering
    const visibilityObs = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) {
            clearTimeout(resumeTimer);
            stopAutoplay();
            isPaused.value = true;
        } else if (!userPaused.value && !reducedMotion.value) {
            resumeTimer = setTimeout(() => {
                isPaused.value = false;
                timerKey.value++;
                startAutoplay();
            }, 1000);
        }
    });
    if (sectionRef.value) visibilityObs.observe(sectionRef.value);
});

onUnmounted(() => {
    stopAutoplay();
    clearTimeout(resumeTimer);
    clearTimeout(transitionCleanup);
});
</script>

<template>
    <section ref="sectionRef" aria-label="Notable moments"
        class="block-notable-moments relative overflow-hidden isolate
        flex flex-col-reverse md:flex-row">

        <!-- LEFT: content column — text centered, controls bottom-pinned on desktop -->
        <div class="relative z-[3] flex-1 flex flex-col
            gap-[43px] px-[24px] pb-[60px] pt-0
            md:gap-0 md:pl-[40px] md:pr-[40px] md:py-[50px]
            text-white"
            role="group" aria-roledescription="carousel"
            :aria-label="`Notable moment ${currentIndex + 1} of ${slides.length}`">

            <div class="md:flex-1 md:flex md:flex-col md:justify-center md:mb-[51px]">
            <div class="nm-text-container" aria-live="polite" aria-atomic="true">
                <div v-for="(slide, i) in slides" :key="i"
                    :class="['nm-text-slide', getTextSlideClass(i)]"
                    :inert="i !== currentIndex"
                    :aria-hidden="i !== currentIndex">
                    <div class="flex flex-col gap-[14px] md:gap-[18px] md:max-w-[1110px]">
                        <h2 class="font-hubspot-serif normal-case font-normal
                            text-[calc(34/var(--base-fs)*1rem)] leading-[1.1] tracking-normal
                            md:text-[calc(60/var(--base-fs)*1rem)]"
                            v-html="stripBlockTags(slide.headline)" />
                        <div class="font-hubspot-sans font-light normal-case
                            text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]
                            md:text-[calc(16/var(--base-fs)*1rem)] md:leading-[26px]"
                            v-html="slide.text">
                        </div>
                    </div>

                    <!-- CTA Button -->
                    <a v-if="slide.buttonLabel"
                        :href="slide.buttonUrl"
                        class="inline-flex items-center gap-[10px] px-[20px] py-[10px]
                            bg-white text-[var(--magenta)] no-underline
                            rounded-[50px] font-hubspot-serif
                            text-[calc(16/var(--base-fs)*1rem)] leading-[1.3] tracking-[0.32px]
                            md:text-[calc(17/var(--base-fs)*1rem)] md:leading-[1.4] md:tracking-[0.34px]
                            hover:bg-(--orange) hover:text-white transition-colors duration-200
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
                            focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--magenta)]
                            self-start">
                        {{ slide.buttonLabel }}
                    </a>
                </div>
            </div>
            </div>

            <!-- Carousel Controls — only when there's more than one slide -->
            <div v-if="hasMultiple" class="flex gap-[19px] items-center md:mt-auto">
                <!-- Prev/Next pill -->
                <div class="flex w-[86px] md:w-auto gap-[16px] items-center justify-center px-[16px] py-[7px] h-[42px]
                    rounded-[40px] border border-[#fcc3dc]">
                    <button type="button" aria-label="Previous moment"
                        :disabled="isFirst"
                        :class="['group shrink-0 size-[15px] md:size-[13px] bg-transparent border-none p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm', isFirst ? 'opacity-50 cursor-default' : 'cursor-pointer']"
                        @click="goPrev">
                        <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                            <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                                stroke="#fcc3dc" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                class="transition-[stroke] duration-200 group-hover:stroke-white" />
                        </svg>
                    </button>
                    <button type="button" aria-label="Next moment"
                        :disabled="isLast"
                        :class="['group shrink-0 size-[15px] md:size-[13px] bg-transparent border-none p-0 -scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm', isLast ? 'opacity-50 cursor-default' : 'cursor-pointer']"
                        @click="goNext">
                        <svg viewBox="0 0 14.5 14.5" fill="none" aria-hidden="true" class="size-full">
                            <path d="M13.75 7.25H0.75M0.75 7.25L6.88889 0.750002M0.75 7.25L6.88889 13.75"
                                stroke="#fcc3dc" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                class="transition-[stroke] duration-200 group-hover:stroke-white" />
                        </svg>
                    </button>
                </div>

                <!-- Pause/Play button with timer ring -->
                <button type="button"
                    :aria-label="isPaused ? 'Play carousel' : 'Pause carousel'"
                    class="group relative size-[42px] shrink-0 cursor-pointer bg-transparent border-none p-0
                        transition-colors duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-full"
                    @click="togglePause">
                    <!-- Timer ring / static border -->
                    <svg viewBox="0 0 42 42" fill="none" aria-hidden="true" class="absolute inset-0 size-full">
                        <circle v-if="isPaused"
                            cx="21" cy="21" r="20"
                            stroke-width="1"
                            class="stroke-[#fcc3dc] fill-transparent transition-[fill,stroke] duration-200 group-hover:fill-white group-hover:stroke-white" />
                        <circle v-else :key="timerKey"
                            cx="21" cy="21" r="20"
                            stroke-width="1"
                            transform="rotate(-90 21 21)"
                            class="stroke-[#fcc3dc] fill-transparent nm-timer-ring transition-[fill,stroke] duration-200 group-hover:fill-white group-hover:stroke-transparent"
                            :stroke-dasharray="CIRCUMFERENCE"
                            :stroke-dashoffset="CIRCUMFERENCE" />
                    </svg>
                    <!-- Pause icon -->
                    <svg v-if="!isPaused" aria-hidden="true"
                        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[9px] h-[11px]"
                        viewBox="0 0 9 11" fill="none">
                        <rect width="2.5" height="11" rx="0.5" class="fill-[#fcc3dc] transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                        <rect x="6.5" width="2.5" height="11" rx="0.5" class="fill-[#fcc3dc] transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                    </svg>
                    <!-- Play icon -->
                    <svg v-else aria-hidden="true"
                        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ml-[1px] w-[9px] h-[11px]"
                        viewBox="0 0 9 11" fill="none">
                        <path d="M9 5.5L0 11V0L9 5.5Z" class="fill-[#fcc3dc] transition-[fill] duration-200 group-hover:fill-(--magenta)" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Mobile top shard -->
        <img :src="topShardMobilePng" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="absolute top-0 left-0 z-[2] pointer-events-none md:hidden" />

        <!-- RIGHT: image column — fixed proportional width with min/max -->
        <div class="relative z-[1] pt-[56px] pl-[24px] mb-[32px]
            md:mb-0 md:pl-0 md:w-[49.3%] md:max-w-[710px] md:min-w-[300px] md:shrink-0
            md:flex md:items-end md:pt-[111px]">
            <div ref="imgGridRef" class="w-full">
            <Transition :name="imgTransitionName" mode="out-in"
                @before-leave="onImgBeforeLeave" @after-enter="onImgAfterEnter">
                <MessImage :key="currentIndex"
                    :image="{ src: currentSlide.image, alt: currentSlide.imageAlt }"
                    :mobile-image="currentSlide.mobileImage ? { src: currentSlide.mobileImage } : null"
                    lazy class="w-full" />
            </Transition>
            </div>
            <img :src="topShardPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
                :class="['hidden md:inline-block absolute left-0 z-[1] pointer-events-none h-auto -ml-[125px]',
                    shardVisible ? 'shard-slide-in' : 'shard-hidden']"
                style="width: min(746px, 100%);" />
        </div>

        <!-- Decorative: background blur glow (removed per QA — phantom orange) -->
    </section>
</template>

<style>
/* Gradient background: purple → magenta on mobile, magenta → purple → orange on desktop */
.block-notable-moments {
    background-image: linear-gradient(
        193.39deg,
        #ff4800 -5%,
        var(--purple) 25%,
        var(--purple) 68.8%,
        var(--magenta) 99.24%
    );
}

@media (min-width: 768px) {
    .block-notable-moments {
        background-image: linear-gradient(
            110deg,
            var(--magenta) 0%,
            #820ca7 43%,
            #ff4800 97%
        );
    }
}

.shard-hidden {
    top: -310px;
}

.shard-slide-in {
    top: 0;
    transition: top 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

/* --- Text slides: grid stack (all slides always in DOM) --- */

.nm-text-container {
    display: grid;
    align-items: start;
}

.nm-text-slide {
    grid-area: 1 / 1;
    display: flex;
    flex-direction: column;
    gap: 26px;
}

@media (min-width: 768px) {
    .nm-text-container { align-items: end; }
    .nm-text-slide { gap: 31px; }
}

.nm-text-active {
    opacity: 1;
    transform: translateX(0);
    visibility: visible;
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.nm-text-entering-next {
    opacity: 0;
    transform: translateX(30px);
    visibility: visible;
    transition: none;
}

.nm-text-entering-prev {
    opacity: 0;
    transform: translateX(-30px);
    visibility: visible;
    transition: none;
}

.nm-text-leaving-next {
    opacity: 0;
    transform: translateX(-30px);
    visibility: visible;
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.nm-text-leaving-prev {
    opacity: 0;
    transform: translateX(30px);
    visibility: visible;
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.nm-text-hidden {
    opacity: 0;
    visibility: hidden;
    transition: none;
}

/* --- Image transitions (Vue <Transition>) --- */

.nm-img-enter-active, .nm-img-leave-active,
.nm-img-prev-enter-active, .nm-img-prev-leave-active {
    transition: opacity 0.5s ease, transform 0.5s ease;
}

.nm-img-enter-from  { opacity: 0; transform: translateX(100px); }
.nm-img-leave-to    { opacity: 0; transform: translateX(100px); }

.nm-img-prev-enter-from  { opacity: 0; transform: translateX(100px); }
.nm-img-prev-leave-to    { opacity: 0; transform: translateX(100px); }

.mess-image {
    display: block;
}

/* Timer ring animation */
.nm-timer-ring {
    animation: nm-timer-countdown 10s linear forwards;
}

@keyframes nm-timer-countdown {
    to { stroke-dashoffset: 0; }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
    .shard-hidden { top: 0; }
    .shard-slide-in { transition: none; }
    .nm-timer-ring {
        animation: none !important;
        stroke-dashoffset: 0 !important;
    }
    .nm-text-active,
    .nm-text-leaving-next,
    .nm-text-leaving-prev {
        transition: none !important;
    }
    .nm-img-enter-active, .nm-img-leave-active,
    .nm-img-prev-enter-active, .nm-img-prev-leave-active {
        transition: none !important;
    }
    .nm-img-enter-from, .nm-img-leave-to,
    .nm-img-prev-enter-from, .nm-img-prev-leave-to {
        opacity: 1;
        transform: none;
    }
}
</style>
